import {
  DeviceLastReadingDto,
  DisplayUnit,
  EventValueRange,
  EventValueRangeConfig,
  Port,
  PortType,
  Sensor,
  SensorType,
} from '@thingslog/repositories';
import OnOffSensor from '@thingslog/repositories/src/sensor/OnOffSensor';
import DeviceIconStyles from '@thingslog/repositories/src/valueRanges/DeviceIconStyles';
import { Styles } from '@thingslog/repositories/src/valueRanges/Styles';
import { SensorUtils } from '@thingslog/utils';
import _ from 'lodash';

export class SensorDisplayManager {
  public static getSensorInfo = ({
    ports,
    readings,
    valueRanges,
    exclude = {},
  }: SensorDisplayManagerProps): SensorInfo[] => {
    const sensors: SensorInfo[] = [];

    if (!ports) {
      return sensors;
    }

    const { portTypes: excludedPortTypes, sensorTypes: excludedSensorTypes } = exclude;
    Object.entries(ports).forEach(([portIndex, port]: [string, Port]) => {
      const { sensor, enabled } = port;
      const { description, name, visible } = sensor;
      const index = Number(portIndex);

      if (enabled === false || visible === false) return;
      if (excludedPortTypes && excludedPortTypes.includes(port['@type'])) return;
      if (excludedSensorTypes && excludedSensorTypes.includes(sensor['@type'])) return;

      const allValueRanges = valueRanges?.rangeEventConfigDtoList || [];
      const sensorValueRanges = allValueRanges.filter(
        (range: EventValueRange) => range.sensorIndex === index
      );

      sensors.push({
        index: index,
        name: name,
        label: this.getSensorInfoLabel(sensor, index, readings || []),
        description: description,
        color: this.getSensorInfoColor(sensor, index, readings || []),
        valueRanges: this.sortEventValueRanges(sensorValueRanges),
      });
    });

    return sensors;
  };

  private static sortEventValueRanges = (arr: EventValueRange[]): EventValueRange[] => {
    return arr.sort((a: EventValueRange, b: EventValueRange) => {
      // Use non-null min or max, prioritize min for sorting
      const aValue = a.min !== null ? a.min : a.max!;
      const bValue = b.min !== null ? b.min : b.max!;

      if (aValue < bValue) return -1;
      if (aValue > bValue) return 1;

      // If min values are equal, compare max, considering nulls as lesser
      if (a.max === null) return -1;
      if (b.max === null) return 1;
      if (a.max! < b.max!) return -1;
      if (a.max! > b.max!) return 1;

      return 0; // equal values
    });
  };

  private static getSensorInfoLabel = (
    sensor: Sensor,
    index: number,
    lastReadings: DeviceLastReadingDto[]
  ): string => {
    let label = '--';

    let reading: number | null = null;
    const lastReading = lastReadings.find((r: DeviceLastReadingDto) => r.sensorIndex === index);
    if (lastReading && lastReading.reading !== null) {
      reading = Number(lastReading.reading);
    }

    let state0Label: string | null = null;
    let state1Label: string | null = null;
    if (SensorUtils({ sensor }).isOnOffSensor()) {
      const onOffSensor = sensor as OnOffSensor;
      state0Label = onOffSensor.valueLabels?.[0] || null;
      state1Label = onOffSensor.valueLabels?.[1] || null;
    }

    if (reading !== null && reading === 0 && state0Label !== null) {
      label = state0Label;
    } else if (reading !== null && reading === 1 && state1Label !== null) {
      label = state1Label;
    } else if (reading !== null) {
      label = Number(reading).toString();
      const unit = DisplayUnit[sensor.units];
      if (unit) {
        label += ` ${unit}`;
      }
    }

    return label;
  };

  private static getSensorInfoColor = (
    sensor: Sensor,
    index: number,
    lastReadings: DeviceLastReadingDto[]
  ): string | null => {
    if (sensor.useInSensorsAggregation === false) {
      return null;
    }

    let color: string | null = null;
    const lastReading = lastReadings.find((r: DeviceLastReadingDto) => r.sensorIndex === index);
    if (lastReading?.rangeEventConfig) {
      const deviceIconStyles = lastReading.rangeEventConfig.styles.find(
        (style: Styles) => style['@type'] === 'device-icon'
      ) as DeviceIconStyles | undefined;
      color = deviceIconStyles?.backgroundColor || null;
    }
    return color;
  };
}

export interface SensorInfo {
  index: number;
  name: string;
  label: string;
  description: string | null;
  color: string | null;
  valueRanges: EventValueRange[];
}

interface SensorDisplayManagerProps {
  ports?: Record<number, Port>;
  readings?: DeviceLastReadingDto[];
  valueRanges?: EventValueRangeConfig;
  exclude?: {
    portTypes?: PortType[];
    sensorTypes?: SensorType[];
  };
}
